import { autoinject } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import * as moment from 'moment';
import { AchToken } from '../../../models/ach-token';
import { CardToken } from '../../../models/card-token';
import { State } from '../../../services/global-state';
import { PayrazrService } from '../../../services/payrazr';
import { ValidationHelpers } from '../../../services/validation-helpers';
import { I18N } from 'aurelia-i18n';
import { Customer } from '../../../models/customer';
import { HpfPaymentMethodService } from '../../../services/hpf-payment-method.service';
import { CheckPaymentMethod } from '../../../services/hpf-transaction-service';
import { UpdateCardPaymentModel } from '../../../models/update-card-payment-model';
import { UpdateAchPaymentModel } from '../../../models/update-ach-payment-model';

@autoinject
export class AddPaymentMethod {
	customer: Customer;
	isCreateMethod: any;
	paymentMethodType: any;
	today: Date;
	cardNumber: any;
	expMonth: any;
	expYear: any;
	accountTypes: any[];
	accountType: any;
	accountNumber: any;
	aba: any;
	accountHolderName: any;
	zipCode: any;
	formErrors: any;
	cardInfoKey: any;
	checkInfoKey: any;

	constructor(
		private router: Router,
		private globalState: State,
		private payrazr: PayrazrService,
		private i18n: I18N,
		private hpfPaymentMethodService: HpfPaymentMethodService
	) {
		this.isCreateMethod = false;
		this.paymentMethodType = '';

		this.today = new Date();

		this.cardNumber = '';
		let currentMonth = (this.today.getMonth() + 1).toString().length < 2
			? ('0' + (this.today.getMonth() + 1).toString())
			: (this.today.getMonth() + 1).toString();

		this.expMonth = currentMonth;
		this.expYear = (this.today.getFullYear()).toString();

		this.accountTypes = [
			{
				value: 'PersonalChecking',
				description: this.i18n.i18next.t('personalCheckingAccount', 'Personal Checking Account')
			},
			{
				value: 'PersonalSavings',
				description: this.i18n.i18next.t('personalSavingsAccount', 'Personal Savings Account')
			},
			{
				value: 'BusinessChecking',
				description: this.i18n.i18next.t('businessCheckingAccount', 'Business Checking Account')
			}
		];

		this.accountType = '';
		this.accountNumber = '';
		this.aba = '';

		this.accountHolderName = '';
		this.zipCode = '';
	}

	pad(num, size) {
		let s = '000000000' + num;
		return s.substr(s.length - size);
	}

	get accountTypeValues(): string[] {
		let list = [];
		this.accountTypes.forEach(acct => list.push(acct.value));
		return list;
	}

	get accountTypeDescriptions(): string[] {
		let list = [];
		this.accountTypes.forEach(acct => list.push(acct.description));
		return list;
	}

	get years(): number[] {
		let list = [];
		let thisYear = this.today.getFullYear();
		for (let i = 0; i < 20; ++i) {
			list.push((thisYear + i).toString());
		}
		return list;
	}

	get months() {
		return ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
	}

	get expirationDate() {
		return this.pad(this.expMonth, 2) + this.pad(this.expYear % 100, 2);
	}

	get showCardForm() {
		return this.paymentMethodType === 'Card';
	}

	get showCheckForm() {
		return !this.showCardForm;
	}

	get isControlDisabled() {
		return !this.isCreateMethod;
	}

	get isCreateMode() {
		return this.isCreateMethod;
	}

	get isCard() {
		return this.paymentMethodType === 'Card';
	}

	back() {
		this.router.navigateToRoute('myWallet');
	}

	async validate() {
		this.formErrors = [];

		if (this.isCard && this.isCreateMethod) {
			if (!this.cardNumber) {
				this.formErrors.push(this.i18n.i18next.t('cardNumberNotSet', 'Card Number is not set'));
			} else if (this.cardNumber.length < 13) {
				this.formErrors.push(this.i18n.i18next.t('cardNumberLengthInvalid', 'Card Number length is invalid'));
			} else if (!ValidationHelpers.isValidCreditCardNumber(this.cardNumber)) {
				this.formErrors.push(this.i18n.i18next.t('cardNumberNotValid', 'Card Number is not valid'));
			}

			if (!this.checkExpirationDate()) {
				this.formErrors.push(this.i18n.i18next.t('cardExpirationDateNotValid', 'Card Expiration date is not valid'));
			}

		} else {
			if (this.isCreateMethod) {
				if (!this.aba) {
					this.formErrors.push(this.i18n.i18next.t('bankTransitNumberNotSet', 'Bank Transit Number is not set'));
				} else if (this.aba.length < 9) {
					this.formErrors.push(this.i18n.i18next.t('bankTransitNumberLengthInvalid', 'Bank Transit Number length is invalid'));
				} else if (!ValidationHelpers.isTransitNumber(this.aba)) {
					this.formErrors.push(this.i18n.i18next.t('bankTransitNumberInvalid', 'Bank Transit Number is invalid'));
				}

				if (!this.accountNumber) {
					this.formErrors.push(this.i18n.i18next.t('bankAccountNumberNotSet', 'Bank Account Number is not set'));
				} else if (this.accountNumber.length < 5 || this.accountNumber.length > 17) {
					this.formErrors.push(this.i18n.i18next.t('bankAccountNumberMustBe5To17Digits', 'Bank account number must be 5-17 digits'));
				}

				// Verify ACH account only if both aba and accountNumber are valid.
				if (this.formErrors.length === 0) {
					let checkPaymentMethod = new CheckPaymentMethod();
					checkPaymentMethod.aba = this.aba;
					checkPaymentMethod.accountNumber = this.accountNumber;

					let customerKey = !this.customer ? 0 : this.customer.customerKey;

					let isCheckAccepted = await this.hpfPaymentMethodService.verifyCheckAccepted(
						customerKey,
						checkPaymentMethod,
						'C')
						.then(response => response.isSuccessful);

					if (!isCheckAccepted) {
						this.formErrors.push(
							this.i18n.i18next.t(
								'verifyCheckAcceptedConsumerError',
								'Our payment processor, REPAY, cannot validate your bank account information. Please use a different form of payment to complete your transaction.'));
					}
				}
			}
		}

		if (!this.accountHolderName) {
			this.formErrors.push(this.i18n.i18next.t('nameOnAccountNotSet', 'Name on account is not set.'));
		} else {
			const AccountNameHasAtLeastTwoNamesRegex = /[A-Za-z] +[A-Za-z]/;
			if (!AccountNameHasAtLeastTwoNamesRegex.test(this.accountHolderName)) {
				this.formErrors.push(this.i18n.i18next.t('enterBothFirstAndLastName', 'Please enter both a first and last name'));
			}
		}

		if (!this.zipCode) {
			this.formErrors.push(this.i18n.i18next.t('zipCodeNotSet', 'Zipcode is not set'));
		} else if (this.zipCode.length < 5 || (this.zipCode.length > 5 && this.zipCode.length < 9)) {
			this.formErrors.push(this.i18n.i18next.t('zipCodeIsInvalid', 'Zip Code is Invalid.'));
		}

		let isValid = this.formErrors.length === 0;

		if (!isValid) {
			const validationSummary = document.getElementsByTagName('validation-summary')[0] as HTMLElement;
			setTimeout(() => { // wait until it loads to focus
				validationSummary?.focus();
			}, 300);
		}

		return isValid;
	}

	checkExpirationDate() {
		let now = moment();
		let expMonth = this.expMonth - 1;
		let exp = moment({ y: this.expYear, M: expMonth, d: 1 }).add(1, 'months').subtract(1, 'days');
		return exp >= now;
	}

	async save() {
		if (await this.validate()) {
			if (this.isCreateMode) {
				if (this.isCard) {
					this.addNewCard();
				} else {
					this.addNewCheck();
				}
			}
			else {
				if (this.isCard) {
					this.updateCard();
				} else {
					this.updateCheck();
				}
			}
		}
	}

	// TODO: test add and update fns
	addNewCard() {
		let cardToken = new CardToken();
		cardToken.cardholderName = this.accountHolderName;
		cardToken.cardNumber = this.cardNumber;
		cardToken.expirationDate = this.expirationDate;
		cardToken.zipCode = this.zipCode;

		this.payrazr.addCardPaymentMethod(cardToken, null, this.customer.id || null).then(response => {
			if (response.isSuccessful) {
				this.router.navigateToRoute('myWallet');
			} else {
				alert(response.message);
			}
		}).catch(error => {
			alert(this.i18n.i18next.t('errorSavingPaymentMethod', 'Error encountered while saving payment method.'));
		});
	}

	updateCard() {
		let cardToken = new CardToken();
		cardToken.cardInfoKey = this.cardInfoKey;
		cardToken.cardholderName = this.accountHolderName;
		cardToken.expirationDate = this.expirationDate;
		cardToken.zipCode = this.zipCode;

		let updateCardPaymentModel = new UpdateCardPaymentModel();
		updateCardPaymentModel.cardToken = cardToken;
		updateCardPaymentModel.customerId = this.globalState.customer.id;

		this.payrazr.updateCardPaymentMethod(updateCardPaymentModel).then(response => {
			if (response.isSuccessful) {
				this.router.navigateToRoute('myWallet');
			} else {
				alert(response.message);
			}
		}).catch(error => {
			alert(this.i18n.i18next.t('errorSavingPaymentMethod', 'Error encountered while saving payment method.'));
		});
	}

	addNewCheck() {
		let achToken = new AchToken();
		achToken.accountName = this.accountHolderName;
		achToken.aba = this.aba;
		achToken.accountNumber = this.accountNumber;
		achToken.accountType = this.accountType;
		achToken.zipCode = this.zipCode;

		this.payrazr.addAchPaymentMethod(achToken, null, this.customer.id || null).then(response => {
			if (response.isSuccessful) {
				this.router.navigateToRoute('myWallet');
			} else {
				alert(response.message);
			}
		}).catch(error => {
			alert(this.i18n.i18next.t('errorSavingPaymentMethod', 'Error encountered while saving payment method.'));
		});
	}

	updateCheck() {
		let achToken = new AchToken();
		achToken.checkInfoKey = this.checkInfoKey;
		achToken.accountName = this.accountHolderName;
		achToken.zipCode = this.zipCode;

		let updateAchPaymentModel = new UpdateAchPaymentModel();
		updateAchPaymentModel.achToken = achToken;
		updateAchPaymentModel.customerId = this.globalState.customer.id;

		this.payrazr.updateAchPaymentMethod(updateAchPaymentModel).then(response => {
			if (response.isSuccessful) {
				this.router.navigateToRoute('myWallet');
			} else {
				alert(response.message);
			}
		}).catch(error => {
			alert(this.i18n.i18next.t('errorSavingPaymentMethod', 'Error encountered while saving payment method.'));
		});
	}

	populateNameAndZipCode(params) {
		this.accountHolderName = params.name;
		this.zipCode = params.zipCode;
	}

	populateCard(params) {
		try {
			this.cardInfoKey = params.cardInfoKey;
			this.cardNumber = params.last4ofCard;
			this.expMonth = params.expirationDate.substr(0, 2);
			this.expYear = '20' + params.expirationDate.substr(3, 2);

			this.populateNameAndZipCode(params);
		}
		catch (err) {
			console.error('Error populating card.');
		}
	}

	populateAch(params) {
		try {
			this.checkInfoKey = params.checkInfoKey;
			this.accountType = params.achAccountType;
			this.accountNumber = params.last4ofAccount;
			this.aba = params.aba;

			this.populateNameAndZipCode(params);
		}
		catch (err) {
			console.error('Error populating ach.');
		}
	}

	activate(params, routeData) {
		this.paymentMethodType = routeData.paymentMethodType;
		this.isCreateMethod = routeData.isCreateMethod;

		if (routeData.account) {
			if (this.isCard) {
				this.populateCard(routeData.account);
			} else {
				this.populateAch(routeData.account);
			}
		}
		else {
			try {
				if (routeData.customer) {
					this.customer = routeData.customer;
					this.accountHolderName = (this.customer.firstName + ' ' + this.customer.lastName).trim();
					this.zipCode = this.customer.primaryAddress.zip;
				} else {
					this.accountHolderName = this.globalState.customer.firstName + ' ' + this.globalState.customer.lastName;
					this.zipCode = this.globalState.customer.primaryAddress.zip;
				}
			}
			catch (err) {
				console.error('Customer information not found.');
			}
		}
	}
}
